import { useMemo } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { GridRenderCellParams } from '@mui/x-data-grid'
import {
  GroupRow,
  GroupTitleFormatter,
  RenderGroupTitle,
  AggregationType
} from '../types'
import { GroupTitle } from './GroupTitle'

interface UseColumnRendererProps {
  originalColumns: any[]
  groupByFields: string[]
  parentInfoFields: string[]
  expandedGroups: Record<string, boolean>
  handleToggleGroup: (groupId: string) => void
  getGroupAggregation: (
    group: GroupRow,
    field: string,
    type: AggregationType
  ) => any
  groupTitleFormatter?: GroupTitleFormatter
  externalRenderGroupTitle?: RenderGroupTitle
}

export function useColumnRenderer({
  originalColumns,
  groupByFields,
  parentInfoFields,
  expandedGroups,
  handleToggleGroup,
  getGroupAggregation,
  groupTitleFormatter,
  externalRenderGroupTitle
}: UseColumnRendererProps) {
  // Process column definitions
  const columns = useMemo(() => {
    return originalColumns.map(col => {
      // 判断字段类型
      const isGroupField = groupByFields.includes(col.field)
      const isParentInfoField = parentInfoFields.includes(col.field)
      const isNumericColumn = col.type === 'number'

      // 创建基础列配置
      let columnConfig = { ...col }

      // 处理分组字段或父信息字段
      if (isGroupField || isParentInfoField) {
        columnConfig.renderCell = (params: GridRenderCellParams) => {
          const row = params.row

          if (row.isGroupRow) {
            // 如果是分组字段，显示分组标题
            if (col.field === row.groupField) {
              return (
                <Box
                  key={`group-${row.id}-${col.field}}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    pl: row.level * 2
                  }}>
                  <IconButton
                    size='small'
                    onClick={e => {
                      e.stopPropagation()
                      handleToggleGroup(row.id)
                    }}
                    sx={{ mr: 1 }}>
                    {expandedGroups[row.id] ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </IconButton>
                  <GroupTitle
                    row={row as GroupRow}
                    groupTitleFormatter={groupTitleFormatter}
                    externalRenderGroupTitle={externalRenderGroupTitle}
                  />
                </Box>
              )
            }
            // 如果是父信息字段，直接显示值
            else if (isParentInfoField) {
              // 如果原始列有自定义renderCell方法，使用它
              if (col.renderCell && typeof col.renderCell === 'function') {
                // 为分组行创建新的参数对象，设置isGroupRow为false以便正常渲染
                const newParams = {
                  ...params,
                  value: row[col.field],
                  row: {
                    ...row,
                    isGroupRow: false // 设置为false以便正常渲染
                  }
                }

                return (
                  <Box
                    sx={{
                      pl: (row.level + 1) * 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                    {col.renderCell(newParams)}
                  </Box>
                )
              }

              // 如果没有自定义renderCell，使用默认渲染
              return (
                <Box
                  sx={{
                    pl: (row.level + 1) * 2,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                  {row[col.field]}
                </Box>
              )
            }
            // 其他列在分组行中不显示
            return null
          }

          // 在子行中，如果是父级的分组字段或父信息字段，则隐藏值
          if (
            row.isChildRow &&
            (row.parentGroupField === col.field ||
              parentInfoFields.includes(col.field))
          ) {
            return null
          }

          // 子行缩进显示
          return (
            <Box
              key={`child-${row.id}-${col.field}}`}
              sx={{ pl: 2, height: '100%' }}>
              {col.renderCell ? col.renderCell(params) : params.value}
            </Box>
          )
        }
      }
      // 处理非分组字段和非父信息字段
      else {
        // 保留原始renderCell函数
        const originalRenderCell = col.renderCell

        columnConfig.renderCell = (params: GridRenderCellParams) => {
          const row = params.row

          // 在分组行中，对于非分组字段和非父信息字段，不显示
          if (row.isGroupRow) {
            // 如果是数字类型列，显示聚合值
            if (isNumericColumn) {
              try {
                const value = getGroupAggregation(row, col.field, 'sum')
                return (
                  <Typography
                    key={`group-num-${row.id}-${col.field}-${Math.random()}`}
                    sx={{ height: '100%' }}>
                    {typeof value === 'number' ? value.toFixed(2) : value}
                  </Typography>
                )
              } catch (error) {
                console.warn('Error in group aggregation:', error)
                return (
                  <Typography
                    key={`group-num-error-${row.id}-${col.field}-${Math.random()}`}
                    sx={{ height: '100%' }}>
                    0.00
                  </Typography>
                )
              }
            }
            return null
          }

          // 对于子行，使用原始renderCell或默认渲染
          return originalRenderCell ? originalRenderCell(params) : params.value
        }

        // 如果是数字类型列，添加安全的valueGetter
        if (isNumericColumn && col.valueGetter) {
          columnConfig.valueGetter = (params: any) => {
            try {
              if (col.valueGetter && params && params.row) {
                // 使用类型断言解决类型问题
                const valueGetterFunction = col.valueGetter as (
                  params: any
                ) => any
                return valueGetterFunction(params)
              }
              return params.value || 0
            } catch (error) {
              console.warn('Error in valueGetter:', error)
              return 0
            }
          }
        }
      }

      return columnConfig
    })
  }, [
    originalColumns,
    groupByFields,
    parentInfoFields,
    expandedGroups,
    handleToggleGroup,
    getGroupAggregation,
    groupTitleFormatter,
    externalRenderGroupTitle
  ])

  return columns
}
